import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiKey, FiEye, FiEyeOff, FiSave, FiCheck, FiAlertCircle,
  FiLink, FiGlobe, FiSettings, FiShield, FiInfo,
  FiVideo, FiStar, FiZap, FiMonitor, FiCopy,
} = FiIcons;

const STORAGE_KEY = 'em_webinar_integrations';
const load = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; } };

const PLATFORMS = [
  {
    id: 'zoom',
    label: 'Zoom',
    emoji: '🔵',
    tagline: 'Industry-standard video conferencing',
    color: '#2D8CFF',
    bg: 'bg-[#2D8CFF]/10',
    text: 'text-[#2D8CFF]',
    border: 'border-[#2D8CFF]/30',
    docsUrl: 'https://marketplace.zoom.us/develop/create',
    fields: [
      { key: 'apiKey',      label: 'API Key',       hint: 'From Zoom Marketplace → Your App → App Credentials' },
      { key: 'apiSecret',   label: 'API Secret',    hint: 'Keep server-side only — used to sign JWT tokens'     },
      { key: 'accountId',   label: 'Account ID',    hint: 'Found in Zoom Dashboard → Account Management'       },
      { key: 'clientId',    label: 'Client ID',     hint: 'OAuth 2.0 Client ID for server-to-server auth'      },
      { key: 'clientSecret',label: 'Client Secret', hint: 'OAuth 2.0 Client Secret — never expose client-side' },
    ],
    webhookEvents: ['meeting.started', 'meeting.ended', 'meeting.participant_joined', 'recording.completed'],
    notes: 'Zoom requires a Server-to-Server OAuth app (not JWT) for production. Generate tokens using Account ID + Client ID + Client Secret via the /oauth/token endpoint.',
  },
  {
    id: 'gmeet',
    label: 'Google Meet',
    emoji: '🟢',
    tagline: 'Seamless Google Workspace integration',
    color: '#34A853',
    bg: 'bg-[#34A853]/10',
    text: 'text-[#34A853]',
    border: 'border-[#34A853]/30',
    docsUrl: 'https://console.cloud.google.com/',
    fields: [
      { key: 'clientId',     label: 'OAuth Client ID',     hint: 'Google Cloud Console → APIs & Services → Credentials' },
      { key: 'clientSecret', label: 'OAuth Client Secret', hint: 'Keep server-side only'                                 },
      { key: 'serviceEmail', label: 'Service Account Email', hint: 'Used for domain-wide delegation (optional)'         },
      { key: 'apiKey',       label: 'API Key (Restricted)', hint: 'For Calendar API calls — restrict to your domain'    },
    ],
    webhookEvents: ['calendar.event.created', 'calendar.event.updated', 'meet.conference.started'],
    notes: 'Enable the Google Calendar API and Google Meet API in Cloud Console. Use OAuth 2.0 with the calendar.events scope to create Meet links automatically.',
  },
  {
    id: 'teams',
    label: 'Microsoft Teams',
    emoji: '🟣',
    tagline: 'Enterprise-grade Microsoft 365 meetings',
    color: '#6264A7',
    bg: 'bg-[#6264A7]/10',
    text: 'text-[#6264A7]',
    border: 'border-[#6264A7]/30',
    docsUrl: 'https://portal.azure.com/',
    fields: [
      { key: 'tenantId',     label: 'Tenant ID',      hint: 'Azure Active Directory → Overview → Tenant ID'              },
      { key: 'clientId',     label: 'Application (Client) ID', hint: 'Azure App Registration → Overview'                 },
      { key: 'clientSecret', label: 'Client Secret',   hint: 'Azure App Registration → Certificates & Secrets'           },
      { key: 'webhookSecret',label: 'Webhook Secret',  hint: 'Used to validate incoming Graph API change notifications'   },
    ],
    webhookEvents: ['onlineMeeting.created', 'callRecord.completed', 'chat.message.created'],
    notes: 'Register an app in Azure AD with OnlineMeetings.ReadWrite.All and Calendars.ReadWrite permissions. Use client credentials flow (client_credentials grant) for app-only access.',
  },
  {
    id: 'jitsi',
    label: 'Jitsi Meet',
    emoji: '🟠',
    tagline: 'Open-source, self-hosted or cloud',
    color: '#EA7D24',
    bg: 'bg-[#EA7D24]/10',
    text: 'text-[#EA7D24]',
    border: 'border-[#EA7D24]/30',
    docsUrl: 'https://jaas.8x8.vc/',
    fields: [
      { key: 'appId',      label: 'App ID (JaaS)',    hint: 'From 8x8 JaaS Dashboard — leave blank for self-hosted'  },
      { key: 'apiKey',     label: 'API Key (JaaS)',   hint: 'Used to sign JWT tokens for room access control'         },
      { key: 'serverUrl',  label: 'Server URL',       hint: 'e.g. https://meet.jit.si or your self-hosted domain'     },
      { key: 'jwtSecret',  label: 'JWT Secret',       hint: 'Secret for signing room tokens (self-hosted deployments)' },
    ],
    webhookEvents: ['conferenceCreated', 'conferenceDestroyed', 'participantJoined', 'recordingStarted'],
    notes: 'For JaaS (8x8 cloud), use the App ID + API Key to mint short-lived JWTs per meeting. For self-hosted Jitsi, configure the JWT secret in your prosody config.',
  },
  {
    id: 'native',
    label: 'Native Live Stream',
    emoji: '🔴',
    tagline: 'Built-in OneFinalLegacy streaming engine',
    color: '#E53E3E',
    bg: 'bg-[#E53E3E]/10',
    text: 'text-[#E53E3E]',
    border: 'border-[#E53E3E]/30',
    docsUrl: null,
    fields: [
      { key: 'rtmpServer',  label: 'RTMP Ingest Server',  hint: 'Your media server RTMP endpoint (e.g. rtmp://live.yourdomain.com/live)' },
      { key: 'hlsBase',     label: 'HLS Playback Base URL', hint: 'CDN base URL for .m3u8 playlist delivery'                            },
      { key: 'streamToken', label: 'Platform Stream Token', hint: 'Shared secret for stream key generation'                             },
      { key: 'cdnApiKey',   label: 'CDN API Key',          hint: 'For cache purge and analytics access on your CDN provider'            },
    ],
    webhookEvents: ['stream.live', 'stream.ended', 'viewer.joined', 'recording.ready'],
    notes: 'The native engine uses WebRTC for low-latency preview and HLS for wide-audience delivery. Configure your media server (e.g. Nginx-RTMP, Wowza, or MediaMTX) and point these fields to it.',
  },
];

const KeyField = ({ label, hint, value, onChange, mono = true }) => {
  const [visible, setVisible] = useState(false);
  const isSecret = label.toLowerCase().includes('secret') || label.toLowerCase().includes('token') || label.toLowerCase().includes('key');
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">{label}</label>
      <div className="relative">
        <input
          type={isSecret && !visible ? 'password' : 'text'}
          placeholder={hint}
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`w-full pl-4 pr-12 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs ${mono ? 'font-mono' : ''}`}
        />
        {isSecret && (
          <button
            type="button"
            onClick={() => setVisible(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy transition-colors"
          >
            <SafeIcon icon={visible ? FiEyeOff : FiEye} className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-text-muted leading-relaxed">{hint}</p>
    </div>
  );
};

const WebhookField = ({ label, value, onChange, hint }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">{label}</label>
      <div className="flex gap-2">
        <div className="relative flex-grow">
          <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input
            type="url"
            placeholder="https://your-server.com/webhooks/..."
            value={value}
            onChange={e => onChange(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs font-mono"
          />
        </div>
        <button
          type="button"
          onClick={copy}
          className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest border-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${copied ? 'bg-green-500 border-green-500 text-white' : 'bg-brand-light border-brand-dark text-text-muted hover:border-accent-gold'}`}
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {hint && <p className="text-[10px] text-text-muted">{hint}</p>}
    </div>
  );
};

const InfoBox = ({ children, color = 'blue', emoji }) => {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    red:    'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`flex items-start gap-3 border rounded-2xl p-4 ${colors[color]}`}>
      <span className="text-base flex-shrink-0">{emoji || 'ℹ️'}</span>
      <p className="text-[10px] leading-relaxed">{children}</p>
    </div>
  );
};

const EventChip = ({ label }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-brand-light rounded-xl border border-brand-dark">
    <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
    <span className="text-[9px] font-mono text-text-muted">{label}</span>
  </div>
);

const SaveToast = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className="fixed bottom-8 right-8 z-[200] bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">Settings Saved</p>
          <p className="text-[10px] opacity-80">Webinar integration settings updated.</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const PlatformCard = ({ platform, isActive, isDefault, onSelect, onSetDefault }) => (
  <button
    onClick={onSelect}
    className={`relative w-full text-left p-5 rounded-[24px] border-2 transition-all group ${isActive ? `${platform.border} bg-white shadow-lg` : 'border-brand-dark bg-brand-light hover:border-accent-gold/40'}`}
  >
    {isDefault && (
      <div className="absolute -top-2 -right-2 bg-accent-gold text-white text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
        <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Default
      </div>
    )}
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 ${platform.bg} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
        {platform.emoji}
      </div>
      <div className="min-w-0">
        <p className={`font-black text-sm ${isActive ? platform.text : 'text-accent-navy'}`}>{platform.label}</p>
        <p className="text-[9px] text-text-muted leading-tight">{platform.tagline}</p>
      </div>
    </div>
    {!isDefault && (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onSetDefault(); }}
        className="text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors flex items-center gap-1"
      >
        <SafeIcon icon={FiStar} className="w-3 h-3" /> Set as Default
      </button>
    )}
  </button>
);

const WebinarIntegrations = () => {
  const saved = load();

  const [activePlatform, setActivePlatform] = useState('zoom');
  const [defaultPlatform, setDefaultPlatform] = useState(saved.defaultPlatform || 'zoom');
  const [showToast, setShowToast] = useState(false);

  const [keys, setKeys] = useState(saved.keys || {});
  const [webhooks, setWebhooks] = useState(saved.webhooks || {});
  const [enabled, setEnabled] = useState(saved.enabled || {
    zoom: true, gmeet: true, teams: true, jitsi: false, native: false,
  });

  const setKey = (platformId, fieldKey, val) =>
    setKeys(prev => ({ ...prev, [platformId]: { ...(prev[platformId] || {}), [fieldKey]: val } }));

  const setWebhook = (platformId, val) =>
    setWebhooks(prev => ({ ...prev, [platformId]: val }));

  const toggleEnabled = (platformId) =>
    setEnabled(prev => ({ ...prev, [platformId]: !prev[platformId] }));

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ defaultPlatform, keys, webhooks, enabled }));
    window.dispatchEvent(new CustomEvent('webinar_integrations_updated', { detail: { defaultPlatform, enabled } }));
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const platform = PLATFORMS.find(p => p.id === activePlatform);
  const platformKeys = keys[activePlatform] || {};

  return (
    <div className="space-y-8">
      <div className="bg-accent-navy text-white rounded-[32px] p-8 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-accent-gold rounded-full -mr-40 -mt-40 blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <SafeIcon icon={FiVideo} className="w-6 h-6 text-accent-gold" />
            <h2 className="text-2xl font-serif">Webinar & Meeting Integrations</h2>
          </div>
          <p className="text-white/60 text-sm font-light max-w-xl">
            Configure API credentials for all supported meeting platforms, set your global default, and manage webhook endpoints for real-time event sync.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-3 flex-shrink-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${PLATFORMS.find(p => p.id === defaultPlatform)?.bg || 'bg-white/10'}`}>
            {PLATFORMS.find(p => p.id === defaultPlatform)?.emoji}
          </div>
          <div>
            <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Global Default</p>
            <p className="text-sm font-bold text-accent-gold">{PLATFORMS.find(p => p.id === defaultPlatform)?.label}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {PLATFORMS.map(p => (
          <div
            key={p.id}
            onClick={() => toggleEnabled(p.id)}
            className={`cursor-pointer p-4 rounded-[20px] border-2 flex items-center gap-3 transition-all ${enabled[p.id] ? `${p.border} bg-white` : 'border-brand-dark bg-brand-light opacity-60'}`}
          >
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${enabled[p.id] ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
            <div className="min-w-0">
              <p className="text-[10px] font-black text-accent-navy truncate">{p.label}</p>
              <p className={`text-[9px] font-bold ${enabled[p.id] ? 'text-green-600' : 'text-text-muted'}`}>
                {enabled[p.id] ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted px-1">Select Platform</p>
          {PLATFORMS.map(p => (
            <PlatformCard
              key={p.id}
              platform={p}
              isActive={activePlatform === p.id}
              isDefault={defaultPlatform === p.id}
              onSelect={() => setActivePlatform(p.id)}
              onSetDefault={() => setDefaultPlatform(p.id)}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activePlatform}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            <div className={`rounded-[28px] border-2 ${platform.border} bg-white p-7 flex items-center justify-between gap-4`}>
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 ${platform.bg} rounded-2xl flex items-center justify-center text-3xl`}>
                  {platform.emoji}
                </div>
                <div>
                  <h3 className={`font-serif text-2xl ${platform.text}`}>{platform.label}</h3>
                  <p className="text-[10px] text-text-muted">{platform.tagline}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {defaultPlatform === activePlatform && (
                  <span className="bg-accent-gold/10 text-accent-gold border border-accent-gold/20 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                    <SafeIcon icon={FiStar} className="w-3 h-3" /> Global Default
                  </span>
                )}
                {platform.docsUrl && (
                  <a
                    href={platform.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors border border-brand-dark rounded-xl px-3 py-2"
                  >
                    <SafeIcon icon={FiGlobe} className="w-3 h-3" /> Docs
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => toggleEnabled(activePlatform)}
                  className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${enabled[activePlatform] ? 'bg-accent-gold' : 'bg-brand-dark'}`}
                >
                  <motion.div
                    animate={{ x: enabled[activePlatform] ? 24 : 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                  />
                </button>
              </div>
            </div>

            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiKey} className="w-4 h-4 text-accent-navy" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">API Credentials</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">Secure key management</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {platform.fields.map(f => (
                  <KeyField
                    key={f.key}
                    label={f.label}
                    hint={f.hint}
                    value={platformKeys[f.key] || ''}
                    onChange={val => setKey(activePlatform, f.key, val)}
                  />
                ))}
              </div>

              <InfoBox color="blue" emoji="🔐">
                {platform.notes}
              </InfoBox>
            </div>

            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiLink} className="w-4 h-4 text-accent-navy" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">Webhook Endpoint</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">Real-time event delivery</p>
                </div>
              </div>

              <WebhookField
                label={`${platform.label} Webhook URL`}
                value={webhooks[activePlatform] || ''}
                onChange={val => setWebhook(activePlatform, val)}
                hint={`Register this URL in your ${platform.label} developer dashboard to receive real-time event notifications.`}
              />

              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Subscribed Events</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {platform.webhookEvents.map(ev => <EventChip key={ev} label={ev} />)}
                </div>
              </div>

              <InfoBox color="amber" emoji="🛡️">
                Always validate incoming webhook payloads using the platform's signing secret or HMAC signature before processing. Never trust unverified event data.
              </InfoBox>
            </div>

            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiStar} className="w-4 h-4 text-accent-gold" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">Default Meeting Platform</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">Used for all new webinars & events</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {PLATFORMS.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setDefaultPlatform(p.id)}
                    className={`p-4 rounded-[20px] border-2 text-center transition-all ${defaultPlatform === p.id ? `${p.border} bg-white shadow-md` : 'border-brand-dark bg-brand-light hover:border-accent-gold/40'}`}
                  >
                    <div className="text-2xl mb-2">{p.emoji}</div>
                    <p className={`text-[10px] font-black ${defaultPlatform === p.id ? p.text : 'text-accent-navy'}`}>{p.label}</p>
                    {defaultPlatform === p.id && (
                      <div className="mt-2 flex items-center justify-center gap-1 text-accent-gold text-[8px] font-black uppercase">
                        <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" /> Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <InfoBox color="green" emoji="✅">
                The selected platform will be pre-filled when creating new webinars or scheduling memorial events. Memorial owners can override this per-event.
              </InfoBox>
            </div>

          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          className="flex items-center gap-3 px-10 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Integration Settings
        </button>
      </div>

      <SaveToast show={showToast} />
    </div>
  );
};

export default WebinarIntegrations;